
import { TitleHeading } from "@/components/helper/TitleHeading"
import { BaseSection, type BaseSectionProps } from "@/components/sections/BaseSection"
import { BsGithub } from "react-icons/bs";
import { FiArrowUpRight, FiInfo, FiX } from "react-icons/fi";
import { FaUsers, FaCheckCircle, FaClock, FaRocket, FaTrophy, FaChartLine } from "react-icons/fa";
import {
    SiReact, SiNextdotjs, SiTypescript, SiTailwindcss, SiEthereum,
    SiNestjs, SiPostgresql, SiNodedotjs, SiSolidity, SiDocker,
    SiAmazon, SiGithubactions, SiGrafana, SiVite,
    SiVercel, SiMysql, SiLangchain,
    SiGooglechrome, SiObsidian
} from "react-icons/si";
import { getProjectLink } from "@/utils/external-links";
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import { IconType } from "react-icons";
import { useState, useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useScrollLock } from "@/hooks/useScrollLock";
import { useFinePointerMotion } from "@/hooks/useFinePointerMotion";
import "../../styles/interactive.css";
import "../../styles/portfolio.css";
import type { TechStackItem } from "@/types/tech-stack";
import { PROJECT_HIGHLIGHT_DURATION_MS, type PortfolioProjectId } from "@/utils/portfolio-highlight";
import { EmphasisText } from "@/components/helper/EmphasisText";

type ProjectTag = "AI" | "Web3" | "Frontend" | "Backend" | "FullStack" | "Mobile"

type ProjectMetric = {
    icon: IconType
    labelKey: string
    value: string
}

type Project = {
    id: PortfolioProjectId
    title: string
    description: ReactNode
    img: string
    tags: ProjectTag[]
    demoUrl?: string
    githubUrl?: string
    featured?: boolean
    problem: ReactNode
    solution: ReactNode
    result: ReactNode
    metrics?: ProjectMetric[]
    techStack?: TechStackItem[]
}

export type PortfolioProps = BaseSectionProps & {
    highlightedProjectId?: PortfolioProjectId | null
    onHighlightComplete?: () => void
}

export default function Portfolio(props: PortfolioProps) {
    const { t } = useTranslation();
    const { highlightedProjectId, onHighlightComplete } = props;

    const projects: Project[] = [
        {
            id: "omen",
            title: t("portfolio.project1Title"),
            description: <EmphasisText i18nKey="portfolio.project1Description" />,
            img: "/omen-preview.webp",
            tags: ["Web3", "FullStack"],
            demoUrl: "https://www.omen.energy/",
            featured: true,
            problem: <EmphasisText i18nKey="portfolio.project1Problem" />,
            solution: <EmphasisText i18nKey="portfolio.project1Solution" />,
            result: <EmphasisText i18nKey="portfolio.project1Result" />,
            metrics: [
                { icon: FaRocket, labelKey: "portfolio.metricsTeamSize", value: "2-3" },
                { icon: FaChartLine, labelKey: "portfolio.metricsDeployment", value: t("portfolio.metricsDeploymentValue1") },
                { icon: FaClock, labelKey: "portfolio.metricsDuration", value: t("portfolio.metricsDurationValue1") },
                { icon: FaCheckCircle, labelKey: "portfolio.metricsVolume", value: t("portfolio.metricsVolumeValue1") },
            ],
            techStack: [
                { icon: SiNextdotjs, name: "Next.js" },
                { icon: SiReact, name: "React" },
                { icon: SiTypescript, name: "TypeScript" },
                { icon: SiTailwindcss, name: "Tailwind CSS" },
                { icon: SiEthereum, name: "ethers.js" },
                { icon: SiNestjs, name: "Nest.js" },
                { icon: SiPostgresql, name: "PostgreSQL" },
                { icon: SiNodedotjs, name: "Node.js" },
                { icon: SiSolidity, name: "Solidity" },
                { icon: SiVercel, name: "Vercel" },
                { icon: SiAmazon, name: "AWS" },
                { icon: SiDocker, name: "Docker" },
                { icon: SiGithubactions, name: "GitHub Actions" },
                { icon: SiGrafana, name: "Grafana" }
            ]
        },
        {
            id: "aiveronica",
            title: t("portfolio.project3Title"),
            description: <EmphasisText i18nKey="portfolio.project3Description" />,
            img: "/aiv.webp",
            tags: ["Web3", "FullStack", "AI"],
            demoUrl: "https://aiveronica.ai/",
            featured: true,
            problem: <EmphasisText i18nKey="portfolio.project3Problem" />,
            solution: <EmphasisText i18nKey="portfolio.project3Solution" />,
            result: <EmphasisText i18nKey="portfolio.project3Result" />,
            metrics: [
                { icon: FaChartLine, labelKey: "portfolio.metricsMintsLabel", value: t("portfolio.metricsMintsValue3") },
                { icon: FaUsers, labelKey: "portfolio.metricsRoleLabel", value: t("portfolio.metricsRoleValue3") },
                { icon: FaTrophy, labelKey: "portfolio.metricsCollabLabel", value: t("portfolio.metricsCollabValue3") },
                { icon: FaRocket, labelKey: "portfolio.metricsChainLabel", value: t("portfolio.metricsChainValue3") },
            ],
            techStack: [
                { icon: SiNextdotjs, name: "Next.js" },
                { icon: SiReact, name: "React" },
                { icon: SiTypescript, name: "TypeScript" },
                { icon: SiTailwindcss, name: "Tailwind CSS" },
                { icon: SiEthereum, name: "ethers.js" },
                { icon: SiNestjs, name: "Nest.js" },
                { icon: SiMysql, name: "MySQL" },
                { icon: SiNodedotjs, name: "Node.js" },
                { icon: SiSolidity, name: "Solidity" },
                { icon: SiLangchain, name: "LangChain" },
                { icon: SiVercel, name: "Vercel" }
            ]
        },
        {
            id: "2vault",
            title: t("portfolio.project4Title"),
            description: <EmphasisText i18nKey="portfolio.project4Description" />,
            img: "/2vault-preview.webp",
            tags: ["AI", "Frontend"],
            demoUrl: getProjectLink('2vault', 'demo'),
            githubUrl: getProjectLink('2vault', 'github'),
            problem: <EmphasisText i18nKey="portfolio.project4Problem" />,
            solution: <EmphasisText i18nKey="portfolio.project4Solution" />,
            result: <EmphasisText i18nKey="portfolio.project4Result" />,
            metrics: [
                { icon: FaRocket, labelKey: "portfolio.metricsStatusLabel", value: t("portfolio.metricsStatusValue4") },
                { icon: FaClock, labelKey: "portfolio.metricsBuildTimeLabel", value: t("portfolio.metricsBuildTimeValue4") },
                { icon: FaUsers, labelKey: "portfolio.metricsRoleLabel", value: t("portfolio.metricsRoleValue4") },
                { icon: FaChartLine, labelKey: "portfolio.metricsPlatformLabel", value: t("portfolio.metricsPlatformValue4") },
            ],
            techStack: [
                { icon: SiReact, name: "React" },
                { icon: SiTypescript, name: "TypeScript" },
                { icon: SiVite, name: "Vite" },
                { icon: SiTailwindcss, name: "Tailwind CSS" },
                { icon: SiGooglechrome, name: "Chrome Extension" },
                { icon: SiNodedotjs, name: "Node.js" },
                { icon: SiObsidian, name: "Obsidian" },
                { icon: SiVercel, name: "Vercel" }
            ]
        },
    ]

    return (
        <BaseSection {...props}>
            <div className="work-head section-head-split">
                <div>
                    <TitleHeading>
                        {t('portfolio.title')}
                    </TitleHeading>
                    <h2 className="section-subtitle">{t('portfolio.latestWorks')}</h2>
                </div>
                <p className="section-lede">
                    {t('portfolio.subtitle')}
                </p>
            </div>

            <ProjectList
                projects={projects}
                inView={props.section.viewHook.inView}
                highlightedProjectId={highlightedProjectId}
                onHighlightComplete={onHighlightComplete}
            />
        </BaseSection>
    )
}

type ProjectListProps = {
    projects: Project[]
    inView: boolean
    highlightedProjectId?: PortfolioProjectId | null
    onHighlightComplete?: () => void
}

/** Hairline project rows; on a fine pointer a preview image trails the cursor over the list. */
function ProjectList({ projects, inView, highlightedProjectId, onHighlightComplete }: ProjectListProps) {
    const finePointer = useFinePointerMotion();
    const listRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const pointerX = useMotionValue(0);
    const pointerY = useMotionValue(0);
    const previewX = useSpring(pointerX, { stiffness: 260, damping: 28, mass: 0.6 });
    const previewY = useSpring(pointerY, { stiffness: 260, damping: 28, mass: 0.6 });

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const rect = listRef.current?.getBoundingClientRect();
        if (!rect) return;
        pointerX.set(event.clientX - rect.left);
        pointerY.set(event.clientY - rect.top);
    };

    return (
        <div
            ref={listRef}
            className="work-list"
            onPointerMove={finePointer ? handlePointerMove : undefined}
            onPointerLeave={() => setActiveIndex(null)}
        >
            {projects.map((project, index) => (
                <ProjectRow
                    key={project.id}
                    project={project}
                    index={index}
                    inView={inView}
                    isHighlighted={highlightedProjectId === project.id}
                    onHighlightComplete={onHighlightComplete}
                    onActivate={() => setActiveIndex(index)}
                />
            ))}

            {finePointer && (
                <motion.div
                    className="work-preview"
                    aria-hidden="true"
                    style={{ x: previewX, y: previewY }}
                    animate={{ opacity: activeIndex === null ? 0 : 1, scale: activeIndex === null ? 0.6 : 1 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                    <motion.div
                        className="work-preview-track"
                        animate={{ y: `${-(activeIndex ?? 0) * 100}%` }}
                        transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
                    >
                        {projects.map((project) => (
                            <img key={project.id} src={project.img} alt="" loading="lazy" decoding="async" />
                        ))}
                    </motion.div>
                </motion.div>
            )}
        </div>
    )
}

type ProjectRowProps = {
    project: Project
    index: number
    inView: boolean
    isHighlighted?: boolean
    onHighlightComplete?: () => void
    onActivate: () => void
}

function ProjectRow({ project, index, inView, isHighlighted = false, onHighlightComplete, onActivate }: ProjectRowProps) {
    const { id, title, description, img, tags, demoUrl, githubUrl } = project;
    const { t } = useTranslation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const prefersReducedMotion = usePrefersReducedMotion();

    useEffect(() => {
        if (!isHighlighted || !onHighlightComplete) {
            return;
        }

        const timeoutId = window.setTimeout(onHighlightComplete, PROJECT_HIGHLIGHT_DURATION_MS);
        return () => window.clearTimeout(timeoutId);
    }, [isHighlighted, onHighlightComplete]);

    return (
        <>
            <motion.article
                data-project-id={id}
                className={`work-row ${isHighlighted ? "project-card-highlight" : ""}`}
                onPointerEnter={onActivate}
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 24 }}
                animate={{ opacity: inView ? 1 : 0, y: inView || prefersReducedMotion ? 0 : 24 }}
                transition={{
                    duration: prefersReducedMotion ? 0.2 : 0.8,
                    ease: [0.16, 1, 0.3, 1],
                    delay: prefersReducedMotion ? 0 : 0.2 + index * 0.08
                }}
            >
                <div className="work-row-media">
                    {img ? (
                        <img src={img} alt={title} width={800} height={600} loading="lazy" decoding="async" />
                    ) : (
                        <span>{t("portfolio.noImage")}</span>
                    )}
                </div>

                <button
                    type="button"
                    className="work-row-main"
                    onClick={() => setIsDialogOpen(true)}
                    aria-label={`${title}: ${t('portfolio.viewDetails')}`}
                    data-cursor-label={t('portfolio.viewDetails')}
                >
                    <h3 className="work-row-title">
                        {title}
                        <FiArrowUpRight className="work-row-arrow" aria-hidden="true" />
                    </h3>
                    <p className="work-row-desc">{description}</p>
                </button>

                <div className="work-row-meta">
                    <ul className="work-row-tags">
                        {tags.map((tag) => (
                            <li key={tag}>{t(`portfolio.projectTag${tag}`)}</li>
                        ))}
                    </ul>
                    <div className="work-row-links">
                        <button type="button" className="work-row-link" onClick={() => setIsDialogOpen(true)}>
                            <FiInfo aria-hidden="true" />
                            <span>{t('portfolio.viewDetails')}</span>
                        </button>
                        {githubUrl && (
                            <a
                                href={githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="work-row-link is-icon"
                                aria-label="View GitHub repository"
                            >
                                <BsGithub aria-hidden="true" />
                            </a>
                        )}
                        {demoUrl && (
                            <a
                                href={demoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="work-row-link"
                                aria-label="View project"
                            >
                                <span>{t('portfolio.viewProject')}</span>
                                <FiArrowUpRight aria-hidden="true" />
                            </a>
                        )}
                    </div>
                </div>
            </motion.article>

            <ProjectDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                project={project}
            />
        </>
    )
}

function ProjectDialog({ isOpen, onClose, project }: { isOpen: boolean, onClose: () => void, project: Project }) {
    const { title, tags, problem, solution, result, metrics, techStack, demoUrl, githubUrl } = project;
    const { t } = useTranslation();
    const prefersReducedMotion = usePrefersReducedMotion();
    const titleId = useId();
    useScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    const story = [
        { labelKey: 'portfolio.problemLabel', text: problem },
        { labelKey: 'portfolio.solutionLabel', text: solution },
        { labelKey: 'portfolio.resultLabel', text: result },
    ];

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.4 }}
                        onClick={onClose}
                        aria-hidden="true"
                        className="panel-backdrop"
                    />

                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        initial={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
                        exit={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        transition={{ duration: prefersReducedMotion ? 0.15 : 0.5, ease: [0.76, 0, 0.24, 1] }}
                        className="panel"
                    >
                        <div className="panel-header">
                            <p className="panel-meta">
                                {tags.map((tag) => (
                                    <span key={tag}>{t(`portfolio.projectTag${tag}`)}</span>
                                ))}
                            </p>
                            <h2 id={titleId} className="panel-title">
                                {title}
                            </h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="panel-close"
                                aria-label="Close dialog"
                            >
                                <FiX aria-hidden="true" />
                            </button>
                        </div>

                        <div className="panel-body">
                            {story.map(({ labelKey, text }) => (
                                <div key={labelKey} className="panel-block">
                                    <h3 className="panel-label">{t(labelKey)}</h3>
                                    <p>{text}</p>
                                </div>
                            ))}

                            {metrics && metrics.length > 0 && (
                                <div className="panel-block">
                                    <h3 className="panel-label">{t('portfolio.metricsLabel')}</h3>
                                    <dl className="panel-stats">
                                        {metrics.map((metric) => (
                                            <div key={metric.labelKey}>
                                                <dt>{t(metric.labelKey)}</dt>
                                                <dd>{metric.value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                            )}

                            {techStack && techStack.length > 0 && (
                                <div className="panel-block">
                                    <h3 className="panel-label">{t('portfolio.techStackTitle')}</h3>
                                    <ul className="panel-chips">
                                        {techStack.map((tech) => {
                                            const Icon = tech.icon;
                                            return (
                                                <li key={tech.name}>
                                                    <Icon aria-hidden="true" />
                                                    {tech.name}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {(demoUrl || githubUrl) && (
                            <div className="panel-footer">
                                {demoUrl && (
                                    <a href={demoUrl} target="_blank" rel="noopener noreferrer" className="button button-dark">
                                        <span>{t('portfolio.viewProject')}</span>
                                        <FiArrowUpRight aria-hidden="true" />
                                    </a>
                                )}
                                {githubUrl && (
                                    <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="button" aria-label="View GitHub repository">
                                        <BsGithub aria-hidden="true" />
                                        <span>GitHub</span>
                                    </a>
                                )}
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    )
}
